// file: services/chatServices/ChatService.ts

import { CHANNEL_TYPE } from "../../Data/SD";
import UserInfo from "../../interfaces/UserInfo";
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { GetHeaders } from "../videoService/VideoService";

export interface MeetingRoomDto {
    id: string;
    roomId: string;
    calendarEventId?: string;
    createdAt: Date;
    startDate: Date;
    title?: string;
}

export interface Conversation {
    id: number;
    conversationId?: number; // For unified API response (maps to id)
    participants: UserInfo[];
    lastMessage?: Message;
    lastReadAt?: Date;
    unreadCount?: number;
    channel?: ChannelDto;
    activeMeeting?: MeetingRoomDto; // Added property
    isChannel?: boolean; // Flag to distinguish channels from conversations
}

export interface ChannelDto {
    channelId: string;
    conversationId: number;
    name: string;
    topic: string | null;
    channelType: CHANNEL_TYPE;
    associatedEntityId: string | null;
    ownerId: string | null;
}

export interface RemoveAdHocFromConversationDto {
    conversationId: number;
    roomId: string;
}

export interface Reaction {
    userId: string;
    reactionType: string
}

export interface Message {
    attachmentUrl: any;
    id: number;
    conversationId: number;
    author: UserInfo;
    messageContent: string;
    createDate: Date;
    attachmentType: string;
    messageType: string;
    reactions?: Reaction[];
    isNew?: boolean,
    callRecordId?: number;
    lastReadAt: number;
}

export interface SendMessageRequest {
    conversationId: number;
    text: string;
    attachmentUrl: string;
}

interface CreateConversationRequest {
    participantIds: string[];
}

/**
 * Gets all conversations for the authenticated user.
 * Auth token is a Firebase or JWT token
 */
export async function getMyConversations(
    authToken: string,
    includeMeetings: boolean = true // Added parameter
): Promise<Conversation[]> {

    // Append query parameter if requested
    const url = `${process.env.REACT_APP_API_BASE_URL}/chat/my-conversations${includeMeetings ? '?includeMeetings=true' : ''}`;

    const response = await fetch(
        url,
        {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        }
    );
    if (!response.ok) {
        throw new Error("Failed to fetch conversations");
    }
    const conversations = await response.json();
    console.log('Conversations', conversations)
    return conversations;
}

/**
 * Gets all conversations for the authenticated user.
 * Auth token is a Firebase or JWT token
 */
export async function GetMyChannelConversationAsync(
    authToken: string,
    userId: string
): Promise<ApiResponse<Conversation[]>> {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/chat/user/${userId}/get-myChannels`,
            {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            }
        );

        return response.json();
    } catch (error) {
        throw error;
    }
}

/**
 * Gets conversations for Two Users.
 * Auth token is a Firebase or JWT token
 */
export async function GetConversationsofTwoUser(
    authToken: string,
    targetedUserId: string
): Promise<ApiResponse<Conversation>> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/privatechats/${targetedUserId}/conversation`,
        {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        }
    );
    if (!response.ok) {
        console.log("Failed to fetch conversations");
    }

    const data = await response.json();
    console.log("response: ", data);
    return data;
}

/**
 * Gets all messages for a particular conversation
 */
export async function getMessagesForConversation(
    authToken: string,
    conversationId: number,
    pageSize: number = 50,
    beforeMessageId?: number
): Promise<Message[]> {

    let url = `${process.env.REACT_APP_API_BASE_URL}/chat/${conversationId}/messages/pagination?pageSize=${pageSize}`;

    if (beforeMessageId) {
        url += `&beforeMessageId=${beforeMessageId}`;
    }
    const response = await fetch(
        url,
        {
            headers: GetHeaders(authToken),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to fetch messages");
    }
    const message = await response.json()
    return message;
}

export async function getConversationById(
    authToken: string,
    conversationId: number
): Promise<Conversation | null> {
    try {

        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/conversation/${conversationId}`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                headers: GetHeaders(authToken),
            }
        );
        return await response.json();
    } catch (error) {
        console.error("Failed to fetch conversation by ID:", error);
        return null;
    }
}

/**
 * Sends a message to a user (this will create a conversation if not exists)
 */
export async function sendMessage(
    authToken: string,
    conversationId: number,
    text: string,
    attachmentUrl?: string
): Promise<{ conversationId: number; messageId: number; attachmentUrl?: string }> {
    console.log('Attachment URL being sent to API: ', attachmentUrl);

    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/send`,
        {
            method: "POST",
            headers: GetHeaders(authToken, true),
            body: JSON.stringify({ conversationId, text, attachmentUrl }),
        }
    );

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send message: ${errorText}`);
    }

    const message = await response.json();
    console.log('Message', message)
    return message;
}

// -------------- CREATE CONVERSATION --------------
export const createConversation = async (
    token: string,
    participantIds: string[]
): Promise<ApiResponse<Conversation>> => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/chat/create-conversation`;

    const response = await fetch(
        url,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ participantIds } as CreateConversationRequest)
        }
    );

    const data = await response.json();
    return data;
};

// -------------- DELETE CONVERSATION --------------
export const deleteConversationAsync = async (
    token: string,
    conversationId: number
): Promise<ApiResponse<string>> => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/chat/delete/conversation/${conversationId}`;

    const response = await fetch(
        url,
        {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        }
    );

    const data = await response.json();
    return data;
};

export async function markConversationAsRead(
    authToken: string,
    conversationId: number
): Promise<ApiResponse<any>> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/${conversationId}/mark-as-read`,
        {
            method: "POST",
            headers: GetHeaders(authToken, true),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to mark conversation as read");
    }

    // Check if response has content before parsing JSON
    const text = await response.text();

    // If response body is empty, return a default success response
    if (!text || text.trim() === "") {
        return {
            data: null,
            statusCode: response.status,
            message: "Conversation marked as read",
            success: true,
        };
    }

    // Response has content, parse it as JSON
    try {
        return JSON.parse(text);
    } catch (error) {
        // If parsing fails, return a default success response
        return {
            data: null,
            statusCode: response.status,
            message: "Conversation marked as read",
            success: true,
        };
    }
}

export async function getAllConversations(
    authToken: string,
    includeMeetings: boolean = true,
    includeArchived: boolean = false
): Promise<ApiResponse<Conversation[]>> {
    try {
        const params = new URLSearchParams();
        if (includeMeetings) {
            params.append('includeMeetings', 'true');
        }
        if (includeArchived) {
            params.append('includeArchived', 'true');
        }

        const queryString = params.toString();
        const url = `${process.env.REACT_APP_API_BASE_URL}/chat/all${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });

        // Handle 403 Permission Denied for archived channels
        if (response.status === 403) {
            const errorData = await response.json().catch(() => ({ message: 'Permission Denied' }));
            return {
                data: [],
                statusCode: 403,
                message: errorData.message || 'You don\'t have permission to view archived channels',
                success: false,
            };
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch conversations: ${errorText}`);
        }

        const responseData = await response.json();

        // Handle both ApiResponse format and direct data format
        let conversations: any[] = [];
        let apiResponse: ApiResponse<Conversation[]>;

        if (responseData.data && Array.isArray(responseData.data)) {
            // Standard ApiResponse format
            conversations = responseData.data;
            apiResponse = {
                data: [],
                statusCode: responseData.statusCode || response.status,
                message: responseData.message || 'Success',
                success: responseData.success !== undefined ? responseData.success : true,
            };
        } else if (Array.isArray(responseData)) {
            // Direct array format (fallback)
            conversations = responseData;
            apiResponse = {
                data: [],
                statusCode: response.status,
                message: 'Success',
                success: true,
            };
        } else {
            throw new Error('Unexpected response format');
        }

        // Normalize the response: map conversationId to id and normalize lastMessage.author
        apiResponse.data = conversations.map((conv: any) => {
            const normalizedConv: any = {
                ...conv,
                id: conv.conversationId || conv.id, // Use conversationId if present, fallback to id
            };

            // Normalize lastMessage: convert userId to author object if needed
            if (normalizedConv.lastMessage) {
                const lastMsg = normalizedConv.lastMessage;

                // If author is missing but userId exists, look up author from participants
                if (!lastMsg.author && lastMsg.userId && normalizedConv.participants) {
                    const authorParticipant = normalizedConv.participants.find(
                        (p: UserInfo) => p.id === lastMsg.userId
                    );
                    if (authorParticipant) {
                        normalizedConv.lastMessage = {
                            ...lastMsg,
                            author: authorParticipant,
                        };
                    }
                }

                // Ensure messageType exists (default to 'USER' if missing, user will add this in backend)
                if (!normalizedConv.lastMessage.messageType) {
                    normalizedConv.lastMessage.messageType = 'USER';
                }

                // Ensure reactions array exists (default to empty array if missing)
                if (!normalizedConv.lastMessage.reactions) {
                    normalizedConv.lastMessage.reactions = [];
                }

                // Ensure attachmentType exists (default to empty string if missing)
                if (normalizedConv.lastMessage.attachmentType === undefined) {
                    normalizedConv.lastMessage.attachmentType = '';
                }
            }

            return normalizedConv;
        });

        return apiResponse;
    } catch (error: any) {
        return {
            data: [],
            statusCode: 500,
            message: error.message || 'Failed to fetch conversations',
            success: false,
        };
    }
}